import * as Sentry from '@sentry/react';

import type { OrderState } from '../types/Order.types';
import type { FormType } from '../types/Wizard.types';
import { orderStateSchema, wizardStateSchema } from './orderStorage.schemas';

const STORAGE_KEY = 'partnerOrder.state';
const WIZARD_STATE_KEY = 'partnerOrder.wizardState';

export interface WizardState {
  currentStep: number;
  billingAddress?: FormType['billingAddress'];
  consents?: FormType['consents'];
  useShippingForBilling: boolean;
  promoCode?: string;
  confirmedBillingAddress?: FormType['confirmedBillingAddress'];
  // Note: paymentIntentId intentionally excluded for security
}

// ============================================================================
// Generic Storage Utilities
// ============================================================================

/**
 * Checks if sessionStorage is available (guards against SSR and privacy mode).
 */
function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

/**
 * Safely parses JSON from sessionStorage, clearing invalid data on failure.
 * @param key - The sessionStorage key to read from
 * @returns Parsed value or undefined if not found/invalid
 */
function safeParseJSON(key: string): unknown | undefined {
  if (!hasSessionStorage()) return undefined;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return undefined;
    return JSON.parse(raw);
  } catch {
    // Clear corrupted storage to prevent repeated failures
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore - storage may be unavailable
    }
    return undefined;
  }
}

/**
 * Safely writes JSON to sessionStorage.
 * @param key - The sessionStorage key to write to
 * @param value - The value to stringify and store
 */
function safeSetJSON(key: string, value: unknown): void {
  if (!hasSessionStorage()) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore - storage may be full or disabled
  }
}

// ============================================================================
// OrderState Load/Save
// ============================================================================

export function loadOrderState(): OrderState | undefined {
  const parsed = safeParseJSON(STORAGE_KEY);
  if (!parsed) return undefined;

  try {
    const validated = orderStateSchema.validateSync(parsed, { strict: true, abortEarly: false });
    return {
      ...validated,
      // payment_info is not persisted (contains sensitive client_secret)
      guardian: validated.guardian ?? null,
      is_rerequisition: validated.is_rerequisition || false,
      paymentIntent: undefined,
      pricing: undefined,
    } as OrderState;
  } catch (error) {
    // Log validation failure
    if (process.env.NODE_ENV === 'development') {
      console.warn('Partner order state validation failed:', error);
    } else {
      Sentry.captureException(error, {
        tags: { component: 'orderStorage', action: 'loadOrderState' },
      });
    }
    return undefined;
  }
}

export function saveOrderState(state: OrderState) {
  // Note: The Provider controls when this is called - it only persists for non-partner routes
  // Partner routes have campaign_code in the URL, so no persistence needed
  // Exclude payment_info from persistence (contains sensitive client_secret)
  const { paymentIntent: _paymentIntent, ...stateToPersist } = state;
  safeSetJSON(STORAGE_KEY, stateToPersist);
}

// ============================================================================
// WizardState Load/Save
// ============================================================================

export function loadWizardState(): WizardState | undefined {
  const parsed = safeParseJSON(WIZARD_STATE_KEY);
  if (!parsed) return undefined;

  try {
    return wizardStateSchema.validateSync(parsed, { strict: true, abortEarly: false }) as WizardState;
  } catch (error) {
    // Log validation failure
    if (process.env.NODE_ENV === 'development') {
      console.warn('Wizard state validation failed:', error);
    } else {
      Sentry.captureException(error, {
        tags: { component: 'orderStorage', action: 'loadWizardState' },
      });
    }
    return undefined;
  }
}

export function saveWizardState(state: WizardState) {
  safeSetJSON(WIZARD_STATE_KEY, state);
}

export function clearWizardState() {
  try {
    sessionStorage.removeItem(WIZARD_STATE_KEY);
  } catch {
    // ignore - storage may be unavailable
  }
}
